import React from 'react';
import { connect } from 'react-redux';
import styled, { StylesProps } from '../../lib/styles';
import { compose, withProps, withState } from 'recompose';
import {
  TravelCollection as TravelCollectionModel,
  HolidayGroup,
} from '../../models/travelCollection';
import memoize from '../../lib/selectorUtils';
import { Ads } from '../../components/presentational/Ad';
import Spacer from '../../components/presentational/Spacer';
import Typography from '../../components/presentational/Typography';
import StoriesBlock from '../../components/presentational/StoriesBlock';
import HolidaysBlock from '../../components/presentational/HolidaysBlock';
import MeetupsBlock from './MeetupsBlock';
import ExternalSearchBlock, {
  ExternalSearchBlockData,
} from './ExternalSearchBlock';
import { trackEvent, withTracking } from '../../lib/ga';
import {
  SHOW_MORE_STORIES,
  SHOW_MORE_HOLIDAYS,
  SHOW_MORE_PAGINATED_HOLIDAYS,
} from './lib/ga';

import { callOnMount } from '../../behaviour/patterns';
import {
  travelCollectionSelector,
  Brand,
  adsSelector,
} from './store/selectors';
import {
  loadMoreArticlesThunk,
  loadMoreHolidaysThunk,
  loadMoreGroupHolidaysThunk,
} from './store/actions';
import travelCollectionStyles from './TravelCollection_styles';
import PopularBlock from './PopularBlock';
import Metadata from './Metadata';
import ValuePropositions from './ValuePropositions';
import { Helmet } from 'react-helmet';
import { pushEvent } from '../../lib/gtm';
import AdSetup from '../../components/presentational/Ad/AdSetup';
import { hasAccountToken } from '../../lib/accountUtils';
import CardBlock from './CardBlock';
import ExplainerBlock from '../Collection/CollectionBlock/Blocks/ExplainerBlock';
import PanelBlock from '../Collection/CollectionBlock/Blocks/PanelBlock';
import HeaderBlock, {
  HeaderBlockData,
} from '../Collection/CollectionBlock/Blocks/HeaderBlock';
import AlgoliaBlock, {
  AlgoliaBlockData,
} from '../Collection/CollectionBlock/Blocks/AlgoliaBlock';
import CarouselBlock, {
  CarouselBlockData,
} from '../Collection/CollectionBlock/Blocks/CarouselBlock';
import CardGridBlock, {
  CardGridBlockData,
} from '../Collection/CollectionBlock/Blocks/CardGridBlock';
import IconContainerBlock, {
  IconContainerBlockData,
} from '../Collection/CollectionBlock/Blocks/IconContainerBlock';
import ContentBlock, {
  ContentBlockData,
} from '../Collection/CollectionBlock/Blocks/ContentBlock';
import SpacerBlock, {
  SpacerBlockData,
} from '../Collection/CollectionBlock/Blocks/SpacerBlock/SpacerBlock';
import TravelHolidayBlock, {
  SuggestedHolidayBlockData,
  TravelHolidayBlockData,
} from '../Collection/CollectionBlock/Blocks/TravelHolidayBlock';
import ValuePropositionsBlock, {
  ValuePropositionsBlockData,
} from '../Collection/CollectionBlock/Blocks/ValuePropositionsBlock';
import {
  StoriesBlockData,
  StoriesBlockProps,
} from '../../components/presentational/StoriesBlock/StoriesBlock';

import ACFContentBlock, {
  ContentBlockProps as ACFContentBlockProps,
} from '../GroupBlock/ContentBlock';
import ACFHolidayBlock, {
  HolidayBlockProps as ACFHolidayBlockProps,
} from '../GroupBlock/HolidayBlock';
import ACFCardGridBlock, {
  CardGridBlockProps as ACFCardGridBlockProps,
} from '../GroupBlock/CardGridBlock';
import ACFColumnBlock, {
  ColumnBlockProps as ACFColumnBlockProps,
} from '../GroupBlock/ColumnBlock';
import ACFContentLayoutBlock, {
  ContentLayoutBlockProps as ACFContentLayoutBlockProps,
} from '../GroupBlock/ContentLayoutBlock';
import ACFMultiSearchBlock, {
  MultiSearchBlockProps as ACFMultiSearchBlockProps,
} from '../GroupBlock/MultiSearchBlock';
import ACFTitleBlock, {
  TitleBlockProps as ACFPageTitleBlockProps,
} from '../GroupBlock/TitleBlock';

export type Block =
  | HeaderBlockData
  | AlgoliaBlockData
  | ExternalSearchBlockData
  | CarouselBlockData
  | CardGridBlockData
  | IconContainerBlockData
  | ContentBlockData
  | SpacerBlockData
  | TravelHolidayBlockData
  | SuggestedHolidayBlockData
  | ValuePropositionsBlockData
  | StoriesBlockData
  | ACFContentBlockProps
  | ACFHolidayBlockProps
  | ACFCardGridBlockProps
  | ACFColumnBlockProps
  | ACFContentLayoutBlockProps
  | ACFMultiSearchBlockProps
  | ACFPageTitleBlockProps;

export interface TravelCollectionProps extends TravelCollectionModel {
  brand?: Brand;
  ads: Ads;
  loadMore: () => any;
  loadMoreHolidays: () => any;
  setLoggedIn: (loggedIn: boolean) => any;
  loggedIn: boolean | undefined;
  loadMoreGroupHolidays: (url: string, groupIndex: number) => any;
  isLoading: boolean;
  blocks: Block[];
  isV2Collection: boolean;
}

export const fetchMoreHolidays = (
  index,
  loader,
  holidayGroup: HolidayGroup
) => {
  loader(
    `/v2/holidays?exclude_ids=[${holidayGroup.backfillExcludes.join(',')}]${
      holidayGroup.backfillCursor ? `&after=${holidayGroup.backfillCursor}` : ''
    }${
      holidayGroup.backfillOption === 'holiday_type'
        ? `&type=${holidayGroup.backfillValue}`
        : ''
    }${
      holidayGroup.backfillOption === 'destination'
        ? `&destination_ids=[${holidayGroup.backfillValue}]`
        : ''
    }${
      holidayGroup.backfillOption === 'holiday_tag'
        ? `&tag_ids=[${holidayGroup.backfillValue}]`
        : ''
    }${
      holidayGroup.holidays.length % 3 !== 0
        ? `&limit=${3 - (holidayGroup.holidays.length % 3) + 3}`
        : `&limit=6`
    }`,
    index
  );
};

const TravelCollection = ({
  brand = 'sas',
  ads,
  slug,
  storiesBlock,
  title,
  communityStories = [],
  holidayGroups = [],
  meetups = [],
  marketingHero,
  loadMoreHolidays,
  loadMoreGroupHolidays,
  isLoading,
  cards,
  search,
  explainer,
  styles,
  content,
  valuePropositions,
  holidaysBlock,
  popularDestinations,
  popularHolidays,
  blocks,
  isV2Collection = false,
}: TravelCollectionProps & StylesProps<typeof travelCollectionStyles>) => (
  <div className={isV2Collection ? 'v2-collection' : ''}>
    <Helmet title={title} titleTemplate="%s - Travel at 60" />
    <Metadata
      link={`https://travelat60.com/collections/${slug}`}
      homepage={slug === 'home'}
      holidays={
        holidayGroups.length > 0
          ? [
              ...holidayGroups.reduce(
                (holidays, { holidays: innerHolidays }) => [
                  ...holidays,
                  ...innerHolidays,
                ],
                []
              ),
              ...popularHolidays,
            ]
          : []
      }
    />

    {blocks.map((block, blockIndex) => {
      // console.log(block.type, 'TravelCollection Block');
      switch (block.type) {
        //  todo: add other blocks types
        case 'header_block':
          return <HeaderBlock block={block} key={blockIndex} />;
        case 'indexed_search_block':
          return <AlgoliaBlock block={block} key={blockIndex} />;
        case 'external_cruise_search_block':
          return <ExternalSearchBlock block={block} key={blockIndex} />;
        case 'card_grid_block':
          return <CardGridBlock block={block} key={blockIndex} />;
        case 'icon_container_block':
          return <IconContainerBlock block={block} key={blockIndex} />;
        case 'content_block':
          return <ContentBlock block={block} brand="tas" key={blockIndex} />;
        case 'travel_holiday_block':
        case 'suggested_holiday_block':
          // console.log(block.holidays, isV2Collection, `${block.type} switch`);
          return (
            isV2Collection && (
              <TravelHolidayBlock
                block={block}
                hideads={true}
                key={blockIndex}
                isV2Collection={isV2Collection}
                collectionConfig={{ ads, loadMoreGroupHolidays }}
              />
            )
          );
        case 'value_propositions_block':
          // console.log(isV2Collection, 'value_propositions_block switch');
          return (
            isV2Collection && (
              <ValuePropositionsBlock
                block={block}
                key={blockIndex}
                isV2Collection={isV2Collection}
              />
            )
          );
        case 'travel_article_block':
          // console.log(isV2Collection, 'value_propositions_block switch');
          const storiesBlock: StoriesBlockProps = { ...block };
          return (
            isV2Collection && (
              <div key={blockIndex} className={styles.container}>
                <Spacer width={1160} space={['m', 'l']}>
                  <StoriesBlock
                    slotKey="stories"
                    name="top_story_section"
                    feature
                    stickyAd={ads.sticky}
                    bottomAd={ads.bottom}
                    articles={storiesBlock.articles}
                    hasMore
                    heading={storiesBlock['title']}
                    subheading={storiesBlock['subtitle']}
                    onShowMore={
                      storiesBlock['linkUrl']
                        ? () => {
                            window.open(storiesBlock['linkUrl'], '_blank');
                            trackEvent(SHOW_MORE_STORIES, {
                              transport: 'beacon',
                            });
                          }
                        : undefined
                    }
                    aside={
                      storiesBlock.aside?.articles &&
                      storiesBlock.aside.articles.length > 0
                        ? {
                            slotKey: 'community-stories',
                            title:
                              storiesBlock.aside.title ?? 'Community reads',
                            articles: storiesBlock.aside.articles,
                          }
                        : undefined
                    }
                    isV2Collection={isV2Collection}
                  />
                </Spacer>
              </div>
            )
          );

        case 'carousel_block':
        case 'acf_carousel_block':
          return (
            <CarouselBlock
              block={{ ...{ brand }, ...block }}
              key={blockIndex}
            />
          );
        case 'spacer_block':
        case 'acf_spacer_block':
          return (
            isV2Collection && <SpacerBlock block={block} key={blockIndex} />
          );

        case 'acf_content_block':
          return <ACFContentBlock key={blockIndex} {...block} />;
        case 'acf_title_block':
          return <ACFTitleBlock key={blockIndex} {...block} />;
        case 'acf_holiday_block':
          return <ACFHolidayBlock key={blockIndex} {...block} />;
        case 'acf_cardgrid_block':
          return <ACFCardGridBlock key={blockIndex} {...block} />;
        case 'acf_columns_block':
          return <ACFColumnBlock key={blockIndex} {...block} />;
        case 'acf_contentlayout_block':
          return <ACFContentLayoutBlock key={blockIndex} {...block} />;
        case 'acf_multisearch_block':
          // console.log('TravelCollection acf_multisearch_block', block);
          return <ACFMultiSearchBlock key={blockIndex} {...block} />;

        default:
          return <div key={blockIndex} />;
      }
    })}

    {!isV2Collection && <AdSetup />}

    {!isV2Collection && marketingHero && !search && (
      <PanelBlock
        block={{
          brand: 'tas',
          backgroundColor: 'secondary',
          title: marketingHero.title,
          description: marketingHero.description,
          imageURL: marketingHero.imageURL,
          imageAlign: 'bottom',
        }}
      />
    )}

    {!isV2Collection && (
      <div className={styles.container}>
        <Spacer width={1160} space={['m', 'l']}>
          {content && (
            <Typography
              className={styles.content}
              brand={brand}
              dangerouslySetInnerHTML={{ __html: content }}
              family={'primary'}
            />
          )}

          {holidayGroups.length > 0 && (
            <HolidaysBlock
              key={holidayGroups[0].title}
              feature={holidayGroups[0].feature}
              holidays={holidayGroups[0].holidays}
              title={holidayGroups[0].title}
              subtitle={holidayGroups[0].subtitle}
              description={holidayGroups[0].description}
              topAd={holidayGroups[0].hideAds ? undefined : ads.top}
              hasMore={
                (holidayGroups[0].pagination === false &&
                  !!holidayGroups[0].linkText &&
                  !!holidayGroups[0].linkURL) ||
                (holidayGroups[0].pagination === true &&
                  holidayGroups[0].backfillOption !== 'none' &&
                  holidayGroups[0].backfillCursor !== -1)
              }
              lazy={false}
              showMoreText={
                holidayGroups[0].linkText
                  ? holidayGroups[0].linkText
                  : 'Show more holidays'
              }
              onShowMore={() => {
                if (holidayGroups[0].pagination) {
                  fetchMoreHolidays(0, loadMoreGroupHolidays, holidayGroups[0]);
                  trackEvent(SHOW_MORE_PAGINATED_HOLIDAYS, {
                    transport: 'beacon',
                  });
                } else {
                  window.open(holidayGroups[0].linkURL, '_blank');
                  trackEvent(SHOW_MORE_HOLIDAYS, {
                    transport: 'beacon',
                  });
                }
              }}
            />
          )}
        </Spacer>
      </div>
    )}

    {!isV2Collection &&
      holidayGroups.length > 0 &&
      valuePropositions &&
      valuePropositions.length > 0 && (
        <ValuePropositions valuePropositions={valuePropositions} />
      )}

    {!isV2Collection && (
      <div className={styles.container}>
        <Spacer width={1160} space={['m', 'l']}>
          {holidayGroups.length > 1 &&
            holidayGroups
              .slice(1)
              .map(
                (
                  {
                    feature,
                    title,
                    subtitle,
                    description,
                    holidays,
                    linkText,
                    linkURL,
                    pagination,
                    backfillCursor,
                    backfillOption,
                    hideAds,
                  },
                  index
                ) => (
                  <HolidaysBlock
                    key={`${title}_${index}`}
                    feature={feature}
                    holidays={holidays}
                    title={title}
                    subtitle={subtitle}
                    description={description}
                    hasMore={
                      (pagination === false && !!linkText && !!linkURL) ||
                      (pagination === true &&
                        backfillOption !== 'none' &&
                        backfillCursor !== -1)
                    }
                    topAd={hideAds ? undefined : ads.top}
                    showMoreText={linkText ? linkText : 'Show more holidays'}
                    lazy={true}
                    onShowMore={() => {
                      if (pagination) {
                        fetchMoreHolidays(
                          index + 1,
                          loadMoreGroupHolidays,
                          holidayGroups[index + 1]
                        );
                        trackEvent(SHOW_MORE_PAGINATED_HOLIDAYS, {
                          transport: 'beacon',
                        });
                      } else {
                        window.open(linkURL, '_blank');
                        trackEvent(SHOW_MORE_HOLIDAYS, {
                          transport: 'beacon',
                        });
                      }
                    }}
                  />
                )
              )}

          {storiesBlock && storiesBlock.stories.length > 0 && (
            <StoriesBlock
              slotKey="stories"
              name="top_story_section"
              feature
              stickyAd={ads.sticky}
              topAd={ads.top}
              bottomAd={ads.bottom}
              articles={storiesBlock.stories}
              hasMore
              heading={storiesBlock.title}
              subheading={storiesBlock.subtitle}
              onShowMore={
                storiesBlock.linkUrl
                  ? () => {
                      window.open(storiesBlock.linkUrl, '_blank');
                      trackEvent(SHOW_MORE_STORIES, {
                        transport: 'beacon',
                      });
                    }
                  : undefined
              }
              aside={
                communityStories && communityStories.length > 0
                  ? {
                      slotKey: 'community-stories',
                      title: 'Community reads',
                      articles: communityStories,
                    }
                  : undefined
              }
            />
          )}
          {holidaysBlock && holidaysBlock.holidays.length > 0 && (
            <HolidaysBlock
              key={holidaysBlock.title}
              holidays={holidaysBlock.holidays}
              title={holidaysBlock.title}
              subtitle={holidaysBlock.subtitle ? holidaysBlock.subtitle : ''}
              isLoading={!!isLoading}
              lazy={true}
              onShowMore={() => !isLoading && loadMoreHolidays()}
              hasMore={!!(holidaysBlock && holidaysBlock.moreHolidaysURL)}
            />
          )}
        </Spacer>
      </div>
    )}

    {!isV2Collection && explainer && <ExplainerBlock block={explainer} />}
    {!isV2Collection &&
      ((popularDestinations && popularDestinations.length > 0) ||
        (popularHolidays && popularHolidays.length > 0)) && (
        <PopularBlock
          slotKey={'popular-things'}
          destinations={popularDestinations}
          holidays={popularHolidays}
        />
      )}

    {!isV2Collection && meetups.length > 0 && (
      <MeetupsBlock meetups={meetups} brand="tas" />
    )}
    {!isV2Collection && cards && cards.length > 0 && (
      <CardBlock cards={cards} />
    )}
  </div>
);

const travelCollectionPropsSelector = memoize(
  travelCollectionSelector,
  adsSelector,
  (travelCollection, ads) => ({ ...travelCollection, ads })
);

const dispatchProps = dispatch => ({
  loadMore: withTracking(SHOW_MORE_STORIES, () => {
    dispatch(loadMoreArticlesThunk());
  }),
  loadMoreGroupHolidays: withTracking(
    SHOW_MORE_HOLIDAYS,
    (url, blockIndex, cursor) => {
      dispatch(loadMoreGroupHolidaysThunk(url, blockIndex, cursor));
    }
  ),
  loadMoreHolidays: withTracking(SHOW_MORE_HOLIDAYS, () => {
    dispatch(loadMoreHolidaysThunk());
  }),
});

const withOnMount = compose(
  withState('loggedIn', 'setLoggedIn', undefined),
  withProps(({ slug, title, setLoggedIn }: TravelCollectionProps) => ({
    onMount: () => {
      pushEvent('travelPageView', {
        virtualPageUrl: location.href,
        pageCategory: 'TravelCollection',
        pageSection: title,
        pageNumber: 1,
        pageTitle: slug,
      });
      setLoggedIn(hasAccountToken());
    },
  })),
  callOnMount('onMount')
);

export default compose(
  connect(travelCollectionPropsSelector, dispatchProps),
  withOnMount,
  styled(travelCollectionStyles)
)(TravelCollection);
